import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Phone, Loader2, Check, FileCheck2 } from "lucide-react";
import SeoHead from "../components/SeoHead";
import CTABand from "../components/CTABand";
import { PageHeader } from "./Services";
import { SITE } from "../lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { key: "type_bien", label: "Type de bien" },
  { key: "surface", label: "Surface du bien" },
  { key: "type_intervention", label: "Intervention souhaitée" },
  { key: "signes", label: "Signes observés" },
  { key: "urgence", label: "Urgence" },
  { key: "coords", label: "Vos coordonnées" },
];

const OPTIONS = {
  type_bien: [
    { v: "maison", l: "Maison individuelle" },
    { v: "appartement", l: "Appartement / copropriété" },
    { v: "echoppe", l: "Échoppe / maison ancienne" },
    { v: "chai", l: "Chai / cave / dépendance" },
    { v: "autre", l: "Autre" },
  ],
  surface: [
    { v: "<50", l: "Moins de 50 m²" },
    { v: "50-100", l: "50 – 100 m²" },
    { v: "100-200", l: "100 – 200 m²" },
    { v: "200+", l: "Plus de 200 m²" },
  ],
  type_intervention: [
    { v: "traitement_termites", l: "Traitement curatif termites" },
    { v: "traitement_charpente", l: "Traitement bois & charpente" },
    { v: "traitement_preventif", l: "Traitement préventif" },
    { v: "merule", l: "Traitement mérule" },
    { v: "humidite", l: "Traitement humidité" },
  ],
  signes: [
    { v: "cordonnets", l: "Cordonnets de terre sur murs" },
    { v: "bois_creux", l: "Bois qui sonne creux" },
    { v: "sciure", l: "Sciure fine / trous" },
    { v: "ailes", l: "Ailes d'insectes au sol" },
    { v: "humidite", l: "Cave / mur très humide" },
    { v: "aucun", l: "Aucun signe (préventif)" },
  ],
  urgence: [
    { v: "immediate", l: "Immédiate (essaimage, vente en cours)" },
    { v: "1mois", l: "Sous 1 mois" },
    { v: "3mois", l: "Sous 3 mois" },
    { v: "info", l: "Demande d'information" },
  ],
};

// Rough estimation logic
function estimate(form) {
  const tarifs = {
    traitement_termites: [3000, 9000],
    traitement_charpente: [3500, 8500],
    traitement_preventif: [2000, 6000],
    merule: [4000, 12000],
    humidite: [2500, 8000],
  };
  const base = tarifs[form.type_intervention] || [0, 0];
  const surfaceCoef = {
    "<50": 0.6,
    "50-100": 0.85,
    "100-200": 1.0,
    "200+": 1.3,
  }[form.surface] || 1;
  const lo = Math.round((base[0] * surfaceCoef) / 50) * 50;
  const hi = Math.round((base[1] * surfaceCoef) / 50) * 50;
  return { lo, hi };
}

export default function Devis() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    type_bien: "",
    surface: "",
    type_intervention: "",
    signes: [],
    urgence: "",
    name: "",
    phone: "",
    email: "",
    city: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);

  const upd = (k, v) => setForm({ ...form, [k]: v });
  const toggleSigne = (v) => {
    const set = new Set(form.signes);
    set.has(v) ? set.delete(v) : set.add(v);
    upd("signes", Array.from(set));
  };

  const canNext = useMemo(() => {
    const k = STEPS[step]?.key;
    if (!k) return false;
    if (k === "coords") return !!form.name && !!form.phone && !!form.email && !!form.city;
    if (k === "signes") return form.signes.length > 0;
    return !!form[k];
  }, [step, form]);

  const est = useMemo(() => estimate(form), [form]);

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/devis`, form);
      // Garantir un objet, même si l'API renvoie une réponse minimale
      setSent(data || { email_sent: false, id: "ok" });
      toast.success("Demande de devis envoyée.", {
        description: data?.email_sent
          ? "Un email a été transmis à notre équipe."
          : "Votre demande est enregistrée, nous revenons vers vous.",
      });
      // Remonter en haut de page pour rendre la confirmation visible
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (_) {}
    } catch (e) {
      toast.error("Erreur lors de l'envoi.", {
        description:
          e?.response?.data?.detail ||
          "Merci de réessayer ou de nous appeler au 07 45 71 15 10.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-[#E8DCC9]/40 min-h-[60vh]" data-testid="devis-success">
        <SeoHead
          title="Demande de devis envoyée — Termite Habitat 33"
          description="Votre demande de devis gratuit a bien été envoyée à Termite Habitat 33."
          canonical={SITE.url + "/devis"}
          noindex
        />
        <div className="container-x py-20 max-w-2xl text-center">
          <div className="w-16 h-16 rounded-full bg-[#7A2E39] text-white grid place-items-center mx-auto mb-6">
            <Check className="w-8 h-8" />
          </div>
          <h1 className="font-serif text-4xl text-gray-900 mb-4">Demande bien reçue.</h1>
          <p className="text-gray-700 mb-3">
            Merci <strong>{form.name || "à vous"}</strong>. Votre demande de devis a été enregistrée et
            transmise à notre équipe Termite Habitat 33.
          </p>
          <p className="text-gray-700 mb-8">
            Nous étudions votre dossier et nous vous rappelons sous <strong>24 h ouvrées</strong> au{" "}
            <strong>{form.phone}</strong>. Pour toute urgence, joignez-nous directement au{" "}
            <a href={`tel:${SITE.phoneRaw}`} className="text-[#7A2E39] font-semibold underline">
              {SITE.phone}
            </a>.
          </p>

          <div className="bg-white border border-[#D5CABD] p-6 rounded-sm text-left mb-8">
            <div className="eyebrow mb-2">Estimation indicative</div>
            <div className="font-serif text-3xl text-gray-900">
              {est.lo.toLocaleString("fr-FR")} – {est.hi.toLocaleString("fr-FR")} € HT
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Fourchette estimée d'après vos réponses. Le devis définitif sera établi après visite gratuite.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-10">
            <a href={`tel:${SITE.phoneRaw}`} className="btn-primary" data-testid="devis-success-call">
              <Phone className="w-4 h-4" /> Appeler {SITE.phone}
            </a>
            <Link to="/" className="btn-ghost" data-testid="devis-success-home">
              Retour à l'accueil
            </Link>
          </div>

          <p className="text-xs text-gray-500">
            Référence interne : <span className="font-mono">{sent.id || "—"}</span>
          </p>
        </div>
      </div>
    );
  }

  const k = STEPS[step].key;

  return (
    <div data-testid="devis-page">
      <SeoHead
        title="Devis gratuit termites Bordeaux & Gironde | Termite Habitat 33"
        description="Estimation gratuite en ligne en 6 questions pour votre traitement termites, bois, mérule ou humidité à Bordeaux et en Gironde. Réponse sous 24 h."
        canonical={SITE.url + "/devis"}
        keywords={["devis termites Bordeaux", "estimation traitement termites", "devis gratuit termites"]}
        breadcrumbs={[
          { name: "Accueil", url: SITE.url + "/" },
          { name: "Devis gratuit", url: SITE.url + "/devis" },
        ]}
      />
      <PageHeader
        eyebrow="Devis gratuit en ligne"
        title="Estimez votre traitement en 6 questions."
        intro="Réponses anonymes, estimation immédiate, devis détaillé envoyé sous 24 h ouvrées. Aucune carte bancaire demandée."
      />
      <section className="container-x py-12 max-w-3xl">
        <div className="bg-white border border-[#D5CABD] rounded-sm shadow-sm">
          {/* Progress */}
          <div className="border-b border-[#D5CABD] px-6 py-4">
            <div className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500">
              <span>Étape {step + 1} / {STEPS.length}</span>
              <span>{STEPS[step].label}</span>
            </div>
            <div className="mt-3 h-1 bg-[#E8DCC9] rounded-full overflow-hidden">
              <div
                className="h-1 bg-[#7A2E39] transition-all"
                style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="p-6 sm:p-8 min-h-[300px]">
            {k === "type_bien" && (
              <Choices value={form.type_bien} onChange={(v) => upd("type_bien", v)} options={OPTIONS.type_bien} />
            )}
            {k === "surface" && (
              <Choices value={form.surface} onChange={(v) => upd("surface", v)} options={OPTIONS.surface} />
            )}
            {k === "type_intervention" && (
              <Choices value={form.type_intervention} onChange={(v) => upd("type_intervention", v)} options={OPTIONS.type_intervention} />
            )}
            {k === "signes" && (
              <Multi values={form.signes} onToggle={toggleSigne} options={OPTIONS.signes} />
            )}
            {k === "urgence" && (
              <Choices value={form.urgence} onChange={(v) => upd("urgence", v)} options={OPTIONS.urgence} />
            )}
            {k === "coords" && (
              <CoordsForm form={form} upd={upd} estimation={est} />
            )}
          </div>

          <div className="border-t border-[#D5CABD] px-6 py-4 flex items-center justify-between">
            <button
              onClick={() => setStep(Math.max(0, step - 1))}
              disabled={step === 0}
              className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 inline-flex items-center gap-1"
              data-testid="devis-prev"
            >
              <ArrowLeft className="w-4 h-4" /> Précédent
            </button>
            {step < STEPS.length - 1 ? (
              <button
                onClick={() => canNext && setStep(step + 1)}
                disabled={!canNext}
                className="btn-primary disabled:opacity-50"
                data-testid="devis-next"
              >
                Continuer <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={!canNext || loading}
                className="btn-primary disabled:opacity-50"
                data-testid="devis-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck2 className="w-4 h-4" />}
                {loading ? "Envoi…" : "Recevoir mon devis"}
              </button>
            )}
          </div>
        </div>

        <p className="text-xs text-gray-500 text-center mt-4">
          Vous préférez nous parler ? Appelez le{" "}
          <a href={`tel:${SITE.phoneRaw}`} className="text-[#7A2E39] underline underline-offset-4">
            {SITE.phone}
          </a>{" "}
          (Lun–Sam · 8h–19h).
        </p>
      </section>
      <CTABand secondary={false} />
    </div>
  );
}

function Choices({ value, onChange, options }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {options.map((o) => (
        <button
          key={o.v}
          onClick={() => onChange(o.v)}
          className={`text-left px-5 py-4 border rounded-sm transition-all ${
            value === o.v
              ? "border-[#7A2E39] bg-[#7A2E39]/5 ring-2 ring-[#7A2E39]/30"
              : "border-[#D5CABD] hover:border-[#7A2E39]"
          }`}
          data-testid={`devis-opt-${o.v}`}
        >
          <span className="font-medium text-gray-900">{o.l}</span>
        </button>
      ))}
    </div>
  );
}

function Multi({ values, onToggle, options }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {options.map((o) => {
        const active = values.includes(o.v);
        return (
          <button
            key={o.v}
            onClick={() => onToggle(o.v)}
            className={`text-left px-5 py-4 border rounded-sm transition-all flex items-center gap-3 ${
              active
                ? "border-[#7A2E39] bg-[#7A2E39]/5 ring-2 ring-[#7A2E39]/30"
                : "border-[#D5CABD] hover:border-[#7A2E39]"
            }`}
            data-testid={`devis-multi-${o.v}`}
          >
            <span
              className={`w-5 h-5 rounded-sm border ${active ? "bg-[#7A2E39] border-[#7A2E39]" : "border-[#D5CABD]"} grid place-items-center`}
            >
              {active && <Check className="w-3.5 h-3.5 text-white" />}
            </span>
            <span className="font-medium text-gray-900">{o.l}</span>
          </button>
        );
      })}
    </div>
  );
}

function CoordsForm({ form, upd, estimation }) {
  const base =
    "w-full bg-white border border-[#D5CABD] rounded-sm px-4 py-3 text-[15px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#7A2E39]";
  return (
    <div>
      <div className="bg-[#E8DCC9]/40 border border-[#D5CABD] p-5 rounded-sm mb-6">
        <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">
          Estimation indicative
        </div>
        <div className="font-serif text-3xl text-gray-900">
          {estimation.lo.toLocaleString("fr-FR")} – {estimation.hi.toLocaleString("fr-FR")} € HT
        </div>
        <p className="text-xs text-gray-600 mt-1">
          Fourchette d'après vos réponses. Devis définitif après visite gratuite à domicile.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs uppercase tracking-widest font-bold text-gray-600 mb-1 block">Nom & prénom *</span>
          <input className={base} value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="devis-name" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest font-bold text-gray-600 mb-1 block">Téléphone *</span>
          <input className={base} type="tel" value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="devis-phone" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest font-bold text-gray-600 mb-1 block">Email *</span>
          <input className={base} type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="devis-email" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest font-bold text-gray-600 mb-1 block">Ville *</span>
          <input className={base} value={form.city} onChange={(e) => upd("city", e.target.value)} placeholder="Bordeaux, Mérignac…" data-testid="devis-city" />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-xs uppercase tracking-widest font-bold text-gray-600 mb-1 block">Précisions (facultatif)</span>
          <textarea className={base + " min-h-[100px] resize-y"} value={form.message} onChange={(e) => upd("message", e.target.value)} data-testid="devis-msg" />
        </label>
      </div>
      <p className="text-xs text-gray-500 mt-4">
        Vos données sont uniquement utilisées pour traiter votre demande. Aucun partage commercial.
      </p>
    </div>
  );
}
